import { copyDocumentSmallDocumentRepresentation, createDocument, createFolder, deleteDocument, deleteDocuments, deleteFolder, getDocuments, getFolders, massPatchDocumentInformation, patchDocument, patchDocumentInformation, patchFolder } from '../api/EnzianDataApi';
import { addNotification } from '../notification/Notificationstore';
import { openDocument } from './DocumentViewStore.svelte';
import { handleStoreError } from './GeneralStore';
import { addQueueTask } from './QueueTaskStore.svelte';
import { buildFilterValue, createSimpleListConfiguration, createSimpleListListFilter, FilterCondition, FilterType, ViewMode } from '../types/ListTypes';
import { _ } from "svelte-i18n";
import { get, writable } from 'svelte/store';
export function functionCreateFolderStore() {
    let folderListStore = createFolderListStore(true, loadBinding);
    let documentListStore = createDocumentListStore();
    /**
     * Function which glues both of them together
     * @param item
     */
    function loadBinding(item) {
        documentListStore.loadFolder(item);
    }
    return {
        get folderListStore() { return folderListStore; },
        get documentListStore() { return documentListStore; }
    };
}
let folderGlobalStore = [];
let globalMemoryIdFolderStore = null;
export function createFolderListStore(useGlobalData, loadBinding) {
    let internalFolderListStore = $state(useGlobalData ? folderGlobalStore : []);
    let internalMemoryIdFolderStore = $state(useGlobalData ? globalMemoryIdFolderStore : null);
    let listFilter = $state(createSimpleListListFilter({
        gridCols: 1,
        localStorageKey: 'folder-list',
        filterOptions: [
            {
                label: get(_)('folderview.lbl_folder_short'),
                key: 'shortName',
                value: null,
                type: 'string'
            },
            {
                label: get(_)('folderview.lbl_filter_folder_name'),
                key: 'name',
                value: null,
                type: 'string'
            },
            {
                label: get(_)('folderview.lbl_filter_show_archived'),
                key: 'archived',
                value: buildFilterValue(false, FilterCondition.EQ, FilterType.AND),
                defaultValue: buildFilterValue(false, FilterCondition.EQ, FilterType.AND),
                type: 'boolean',
                isSorting: false,
                orSearch: false
            }
        ]
    }));
    let listConfig = $state(createSimpleListConfiguration({
        cardModeConfig: {
            amountOfCols: 1,
            gapSize: 2
        },
        header: get(_)('folderview.lbl_header'),
        showEmptyAdd: true,
        allowMultiSelection: false
    }));
    async function loadData() {
        try {
            //We must invert the filter
            const folderList = await getFolders(listFilter);
            //Reset the data.
            if (listFilter.page == 0) {
                internalFolderListStore = folderList;
            }
            else {
                //Merge the lists
                internalFolderListStore.push(...folderList);
            }
            //Keep a memory ref
            folderGlobalStore = internalFolderListStore;
            //Increase the page counter.
            if (internalFolderListStore.length > 0) {
                listFilter.page += 1;
            }
        }
        catch (error) {
            handleStoreError('folderview.err_folder_load', error);
        }
    }
    let storeListeners = $state([]);
    return {
        get data() { return internalFolderListStore; },
        set data(value) { internalFolderListStore = value; },
        get memoryIdFolderStore() { return internalMemoryIdFolderStore; },
        set memoryIdFolderStore(value) {
            internalMemoryIdFolderStore = value;
            globalMemoryIdFolderStore = value;
        },
        get listFilter() { return listFilter; },
        set listFilter(value) { listFilter = value; },
        get listConfig() { return listConfig; },
        set listConfig(value) { listConfig = value; },
        clear() {
            internalFolderListStore = [];
        },
        loadData: loadData,
        addNotifier(listener) {
            storeListeners.push(listener);
        },
        removeNotifier(listener) {
            storeListeners = storeListeners.filter((li) => li == listener);
        },
        async applyFilter() {
            //Reset the start id
            listFilter.page = 0;
            internalMemoryIdFolderStore = null;
            globalMemoryIdFolderStore = null;
            //Load the data.
            await loadData();
            //Select the first folder
            if (internalFolderListStore.length > 0) {
                internalFolderListStore[0].selected = true;
                internalMemoryIdFolderStore = internalFolderListStore[0].id;
                globalMemoryIdFolderStore = internalFolderListStore[0].id;
                loadBinding?.(internalFolderListStore[0]);
            }
            //Inform the listeners
            storeListeners.forEach((listener) => {
                //Call the event
                listener.onFilterApplied();
            });
        },
        /**
         * Create a new folder for this store
         * @param createFolderObject
         */
        async createFolderStore(createFolderObject) {
            const createdFolder = await createFolder(createFolderObject);
            createdFolder.selected = true;
            //Set the selection
            internalFolderListStore.forEach((item) => item.selected = false);
            internalFolderListStore.push(createdFolder);
            internalMemoryIdFolderStore = createdFolder.id;
            //Load the folder content
            loadBinding?.(createdFolder);
        },
        /**
         * Update a given folder
         * @returns
         */
        async patchFolderStore(targetFolder) {
            const request = {
                shortName: targetFolder.shortName,
                name: targetFolder.name,
                archived: targetFolder.archived,
                memo: targetFolder.memo
            };
            let updatedFolder = await patchFolder(request, targetFolder.id);
            //Find the target index.
            let targetIndex = internalFolderListStore.findIndex(folder => folder.id == updatedFolder.id);
            if (targetIndex < 0)
                return;
            //We have to mark the folder as selected before it is updated
            updatedFolder.selected = true;
            //Update the value.
            internalFolderListStore[targetIndex] = updatedFolder;
            //Hide the currently selected folder
            editFolderStore.set(null);
        },
        /**
         * Make the archive state
         * @param showArchived
         * @param targetFolderId
         * @param request
         */
        async patchArchiveState(showArchived, targetFolderId, request) {
            //Update the folder itself
            await patchFolder(request, targetFolderId);
            // remove the folder from the list, if the archived filter is not set to show all,
            // otherwise update the archived state of the folder in the list
            if (showArchived) {
                internalFolderListStore = internalFolderListStore.filter(folder => folder.id != targetFolderId);
                if (internalFolderListStore.length > 0) {
                    internalFolderListStore[0].selected = true;
                    loadBinding?.(internalFolderListStore[0]);
                }
                else {
                    loadBinding?.(null);
                }
            }
            else {
                internalFolderListStore = internalFolderListStore.map(folder => {
                    if (folder.id == targetFolderId) {
                        return { ...folder, archived: request.archived || false };
                    }
                    return folder;
                });
            }
            //Remove the memory for the selected folder
            this.memoryIdFolderStore = null;
        },
        async deleteFolderStore(folderId) {
            const task = await deleteFolder(folderId);
            //Add the task to the stack
            addQueueTask(task);
            //Remove the folder from the list
            internalFolderListStore = internalFolderListStore.filter(f => f.id !== folderId);
            internalFolderListStore[0].selected = true;
            loadBinding?.(internalFolderListStore[0]);
        },
        /**
         * Scroll to the target div in the list with the id we keept in a store to survive
         * page change.
         * @param targetFolderId
         */
        applyMemoryToList() {
            if (internalMemoryIdFolderStore == null)
                return null;
            //Find the last index
            const lastSelectedFolderIndex = internalFolderListStore.findIndex((folder) => folder.id == internalMemoryIdFolderStore);
            if (lastSelectedFolderIndex == null && internalFolderListStore.length > 0) {
                //Select the first one
                internalFolderListStore[0].selected = true;
                loadBinding?.(internalFolderListStore[0]);
            }
            else if (lastSelectedFolderIndex >= 0) {
                //Select the remebered folder
                internalFolderListStore[lastSelectedFolderIndex].selected = true;
                loadBinding?.(internalFolderListStore[lastSelectedFolderIndex]);
            }
            return lastSelectedFolderIndex;
        },
        async massMoveDocuments(requests) {
            await massPatchDocumentInformation(requests);
            //Reload the documents
            this.applyFilter();
        },
        syncGlobalStore() {
            if (useGlobalData) {
                //Make sure the global store is on the same page
                folderGlobalStore = internalFolderListStore;
                globalMemoryIdFolderStore = internalMemoryIdFolderStore;
            }
        }
    };
}
function createDocumentListStore() {
    let displayedFolder = null;
    let internalDocumentListStore = $state([]);
    let storeListeners = $state([]);
    let listConfig = $state(createSimpleListConfiguration({
        header: get(_)('document_list_view.lbl_header'),
        cardModeConfig: {
            amountOfCols: 4
        },
        tableModeConfig: {
            showTableConfig: true
        },
        showViewModeSwitches: [ViewMode.LIST, ViewMode.SMALL, ViewMode.BIG]
    }));
    let listFilter = $state(createSimpleListListFilter({
        localStorageKey: 'folder-document-list',
        filterOptions: [
            {
                label: get(_)('document_list_view.lbl_document_type'),
                key: 'type',
                value: null,
                type: 'string',
                tableOption: {
                    allowToggleVisibility: true
                }
            },
            {
                label: get(_)('document_list_view.lbl_document_short_name'),
                key: 'shortName',
                value: null,
                type: 'string'
            },
            {
                label: get(_)('document_list_view.lbl_document_name'),
                key: 'name',
                value: null,
                type: 'string',
                tableOption: {
                    allowToggleVisibility: true
                }
            },
            {
                label: get(_)('document_list_view.lbl_document_date'),
                key: 'documentDate',
                value: null,
                type: 'date',
                orSearch: false,
                tableOption: {
                    allowToggleVisibility: true
                }
            },
            {
                label: get(_)('document_list_view.lbl_last_modified'),
                key: 'lastModified',
                value: null,
                type: 'date',
                orSearch: false,
                tableOption: {
                    allowToggleVisibility: true
                }
            },
            {
                label: get(_)('document_list_view.lbl_document_total'),
                key: 'total',
                value: null,
                type: 'big',
                bigOptions: {
                    hasFilter: true
                },
                orSearch: false,
                tableOption: {
                    allowToggleVisibility: true
                }
            },
            {
                label: get(_)('document_list_view.lbl_document_regie'),
                key: 'isRegie',
                value: null,
                type: 'boolean',
                orSearch: false,
                tableOption: { showAsTableHeader: false }
            },
            {
                label: get(_)('document_list_view.lbl_document_mobile_editable'),
                key: 'isMobileEditable',
                value: null,
                type: 'boolean',
                orSearch: false,
                tableOption: { showAsTableHeader: false }
            },
            {
                label: get(_)('document_list_view.lbl_is_canceled'),
                key: 'cancelDate',
                value: null,
                type: 'boolean',
                orSearch: false,
                tableOption: { showAsTableHeader: false },
                booleanOption: {
                    useExistMode: true
                }
            },
            {
                label: get(_)('document_list_view.lbl_document_tags'),
                key: 'tags',
                value: null,
                type: 'tags',
                tableOption: {
                    allowToggleVisibility: true
                }
            },
            {
                label: get(_)('document_list_view.lbl_document_status'),
                key: 'status',
                value: null,
                type: 'boolean',
                orSearch: false,
                isSorting: false,
                tableOption: { width: 60, allowToggleVisibility: true },
                showAsFilter: false
            },
            {
                label: '',
                key: 'fkFolder',
                value: null,
                type: 'number',
                showAsFilter: false,
                tableOption: {
                    showAsTableHeader: false
                },
                isSorting: false,
                resetProtect: true
            }
        ]
    }));
    async function loadData() {
        try {
            const documents = await getDocuments(listFilter);
            if (listFilter.page == 0) {
                internalDocumentListStore = documents;
            }
            else {
                internalDocumentListStore.push(...documents);
            }
            //Set the new start id.
            if (internalDocumentListStore.length > 0) {
                listFilter.page += 1;
            }
        }
        catch (error) {
            handleStoreError('document_list_view.err_document_load', error);
        }
    }
    return {
        get data() { return internalDocumentListStore; },
        set data(value) { internalDocumentListStore = value; },
        get listFilter() { return listFilter; },
        set listFilter(value) { listFilter = value; },
        get listConfig() { return listConfig; },
        set listConfig(value) { listConfig = value; },
        addNotifier(listener) {
            storeListeners.push(listener);
        },
        removeNotifier(listener) {
            storeListeners = storeListeners.filter((li) => li == listener);
        },
        loadFolder(folder) {
            //Clear the folder list of none is selcted
            if (!folder) {
                internalDocumentListStore = [];
                listConfig.header = get(_)('document_list_view.lbl_header');
                displayedFolder = null;
                return;
            }
            //Set header
            listConfig.header = `${folder.shortName} - ${get(_)('document_list_view.lbl_header')}`;
            //Make sure the filter targerts the right folder
            const folderFilter = listFilter.filterOptions.find((option) => option.key == 'fkFolder');
            if (folderFilter) {
                folderFilter.value = buildFilterValue(folder.id, FilterCondition.EQ, FilterType.AND);
            }
            displayedFolder = folder;
            this.applyFilter();
        },
        clear() {
            internalDocumentListStore = [];
        },
        async createDocumentStore(request) {
            if (!displayedFolder)
                return;
            const createdDocument = await createDocument(displayedFolder.id, request);
            //Set the new selection
            createdDocument.selected = true;
            internalDocumentListStore.unshift(createdDocument);
            openDocument(createdDocument);
        },
        loadData: loadData,
        async applyFilter() {
            listFilter.page = 0;
            await loadData();
            //Inform the listeners
            storeListeners.forEach((listener) => {
                //Call the event
                listener.onFilterApplied();
            });
        },
        async patchDocumentInformationStore(request, documentInformationId) {
            const patchedDocumentInformation = await patchDocumentInformation(documentInformationId, request);
            //If we have a move requset we have to remove the item from the list
            const index = internalDocumentListStore.findIndex(document => document.id === patchedDocumentInformation.fkDocument);
            if (internalDocumentListStore[index].documentInformation.fkFolder != request.fkFolder) {
                internalDocumentListStore = internalDocumentListStore.filter((item) => item.documentInformation.id != documentInformationId);
            }
            else {
                internalDocumentListStore[index].documentInformation = patchedDocumentInformation;
            }
        },
        async patchDocumentStore(document, documentId) {
            const patchedDocument = await patchDocument(documentId, document);
            const index = internalDocumentListStore.findIndex(f => f.id === documentId);
            internalDocumentListStore[index] = { ...internalDocumentListStore[index], ...patchedDocument };
        },
        async deleteDocumentStore(documentId) {
            try {
                await deleteDocument(documentId);
                // delete the document from the list
                internalDocumentListStore = internalDocumentListStore.filter(f => f.id !== documentId);
                //The delete counter will be managed by the default simple list
                //Inform the user
                addNotification({
                    type: "success",
                    message: get(_)("document_list_view.lbl_delete_document_success")
                });
            }
            catch (err) {
                handleStoreError("document_list_view.err_delete_document", err);
            }
        },
        async deleteDocumentsStore(documentIds) {
            try {
                await deleteDocuments(documentIds);
                // delete the document from the list
                internalDocumentListStore = internalDocumentListStore.filter(f => !documentIds.includes(f.id));
                //Add the delete counter for the correct query.
                listFilter.deletedCounter += documentIds.length;
                //Inform the user
                addNotification({
                    type: "success",
                    message: get(_)("document_list_view.lbl_delete_documents_success")
                });
            }
            catch (err) {
                handleStoreError("document_list_view.err_delete_document", err);
            }
        },
        async copySmallDocumentRepresentationStore(documentId, request) {
            try {
                const documentCopy = await copyDocumentSmallDocumentRepresentation(documentId, request);
                //Deselect all existing ones
                internalDocumentListStore.forEach(doc => {
                    doc.selected = false;
                });
                //Select the new document
                documentCopy.selected = true;
                //Add the element to the store
                internalDocumentListStore.unshift(documentCopy);
                addNotification({
                    message: get(_)('document_list_view.lbl_copy_document_success'),
                    type: 'success'
                });
            }
            catch (err) {
                handleStoreError("document_list_view.err_copy_document", err);
            }
        }
    };
}
export const editFolderStore = writable(null);
